import { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@mui/material';
import { ExpandMore, ExpandLess, Logout, OpenInNew, Home } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { APP_VERSION } from '@/version';

interface ErrorBoundaryProps {
	children?: ReactNode;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
	errorInfo: ErrorInfo | null;
	showDetails: boolean;
	sentryEventId: string | null;
}

function detectBrowser(): { name: string; key: string } {
	const ua = navigator.userAgent;

	if (ua.indexOf('Firefox') > -1) return { name: 'Firefox', key: 'firefox' };

	if (ua.indexOf('Safari') > -1 && ua.indexOf('Chrome') === -1) return { name: 'Safari', key: 'safari' };

	if (ua.indexOf('Chrome') > -1) return { name: 'Chrome', key: 'chrome' };

	if (ua.indexOf('Edge') > -1 || ua.indexOf('Edg') > -1) return { name: 'Edge', key: 'edge' };

	if (ua.indexOf('Opera') > -1 || ua.indexOf('OPR') > -1) return { name: 'Opera', key: 'opera' };

	return { name: 'your browser', key: 'unknown' };
}

const browserGuides = {
	chrome: 'https://support.google.com/chrome/answer/95582',
	firefox: 'https://support.mozilla.org/en-US/kb/clear-browsing-data-firefox',
	safari: 'https://support.apple.com/en-us/HT201265',
	edge: 'https://support.microsoft.com/en-us/microsoft-edge/delete-browsing-data-from-microsoft-edge',
	opera: 'https://www.opera.com/help/tutorials/security/privacy/'
};

function ErrorBoundaryWithHooks(props: { state: ErrorBoundaryState; toggleDetails: () => void; handleRefresh: () => void; resetError: () => void }) {
	const navigate = useNavigate();
	const { state, toggleDetails, handleRefresh, resetError } = props;
	const { error, errorInfo, showDetails, sentryEventId } = state;
	const isProduction = !import.meta.env.DEV;
	const currentBrowser = detectBrowser();

	const handleHardRefresh = () => {
		// Clear all caches
		if ('caches' in window) {
			caches.keys().then((names) => {
				names.forEach((name) => caches.delete(name));
			});

		}
		sessionStorage.clear();
		window.location.reload();
	};

	const handleSignOut = () => {
		resetError();
		localStorage.clear();
		sessionStorage.clear();
		navigate('/sign-in');
	};

	const handleGoHome = () => {
		resetError();
		navigate('/');
	};

	const browserLinks = [
		{ name: 'Chrome', key: 'chrome' },
		{ name: 'Firefox', key: 'firefox' },
		{ name: 'Safari', key: 'safari' },
		{ name: 'Edge', key: 'edge' },
		{ name: 'Opera', key: 'opera' }
	];

	return (
		<div className="bg-white p-24 w-full">
			<div className="w-full">
				<h1 className="text-5xl font-semibold mb-8 text-gray-900">An Error Occurred</h1>

				<div className="mb-10 space-y-6">
					<p className="text-xl text-gray-700">
						We encountered an unexpected issue. Please try the following:
					</p>

					<div className="bg-gray-50 p-8 rounded-lg space-y-6 border border-gray-200">
						<div className="flex items-start gap-6">
							<span className="font-semibold text-lg text-gray-900 min-w-fit">1.</span>
							<div className="w-full">
								<Button
									onClick={handleRefresh}
									variant="text"
									sx={{
										textTransform: 'none',
										fontSize: '1rem',
										padding: 0,
										justifyContent: 'flex-start',
										color: '#1f2937',
										'&:hover': {
											backgroundColor: 'transparent',
											textDecoration: 'underline'
										}
									}}
								>
									Refresh the page
								</Button>
								<span className="text-lg text-gray-700"> and clear your browser cache</span>
								<div className="mt-4">
									<p className="text-sm text-gray-600 mb-4">Select your browser for cache clearing instructions (opens in new tab):</p>
									<div className="space-y-4">
										{browserLinks.map((browser) => (
											<a
												key={browser.key}
												href={browserGuides[browser.key as keyof typeof browserGuides]}
												target="_blank"
												rel="noopener noreferrer"
												className={`flex items-center gap-3 px-5 py-4 rounded text-sm font-medium transition-colors cursor-pointer ${currentBrowser.key === browser.key
													? 'bg-blue-100 border-2 border-blue-600 text-blue-900'
													: 'bg-gray-100 border border-gray-300 text-gray-800 hover:bg-gray-200'
													}`}
											>
												<div className="flex-1 min-w-0">
													<div className="flex items-center gap-2 mb-2">
														<span className="font-semibold text-base">{browser.name}</span>
														{currentBrowser.key === browser.key && (
															<span className="inline-block bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap">
																YOUR BROWSER
															</span>
														)}
													</div>
													<span className="font-mono text-xs break-all block text-gray-600">
														{browserGuides[browser.key as keyof typeof browserGuides]}
													</span>
												</div>
												<OpenInNew className="flex-shrink-0 ml-2" />
											</a>
										))}
									</div>
								</div>
							</div>
						</div>

						<div className="h-px bg-gray-200"></div>

						<div className="flex items-start gap-6">
							<span className="font-semibold text-lg text-gray-900 min-w-fit">2.</span>
							<p className="text-lg text-gray-700">
								Contact our support team if the issue persists.
							</p>
						</div>
					</div>
				</div>

				{isProduction && sentryEventId && (
					<div className="mb-10 p-6 bg-gray-50 border border-gray-200 rounded-lg">
						<p className="text-base text-gray-700 mb-2">
							<span className="font-semibold">Error Reference ID:</span>{' '}
							<code className="bg-white px-3 py-2 rounded text-gray-800 font-mono text-sm border border-gray-300">
								{sentryEventId}
							</code>
						</p>
						<p className="text-sm text-gray-600">
							Please provide this ID when contacting support.
						</p>
					</div>
				)}

				<div className="flex gap-4">
					<Button
						onClick={toggleDetails}
						variant="outlined"
						sx={{
							textTransform: 'none',
							fontSize: '1rem',
							borderColor: '#d1d5db',
							color: '#374151',
							'&:hover': {
								borderColor: '#9ca3af',
								backgroundColor: '#f9fafb'
							}
						}}
						startIcon={showDetails ? <ExpandLess /> : <ExpandMore />}
					>
						{showDetails ? 'Hide' : 'Show'} Details
					</Button>

					<Button
						onClick={handleGoHome}
						variant="outlined"
						sx={{
							textTransform: 'none',
							fontSize: '1rem',
							borderColor: '#d1d5db',
							color: '#374151',
							'&:hover': {
								borderColor: '#9ca3af',
								backgroundColor: '#f9fafb'
							}
						}}
						startIcon={<Home />}
					>
						Go Home
					</Button>

					<Button
						onClick={handleSignOut}
						variant="outlined"
						sx={{
							textTransform: 'none',
							fontSize: '1rem',
							borderColor: '#d1d5db',
							color: '#374151',
							'&:hover': {
								borderColor: '#9ca3af',
								backgroundColor: '#f9fafb'
							}
						}}
						startIcon={<Logout />}
					>
						Sign Out
					</Button>
				</div>

				{showDetails && (
					<div className="mt-10 p-6 bg-gray-50 rounded-lg overflow-auto border border-gray-200">
						<p className="text-base font-mono text-gray-800 whitespace-pre-wrap break-words mb-6 bg-white p-4 rounded border border-gray-300">
							{error && error.toString()}
						</p>
						{errorInfo && (
							<details>
								<summary className="cursor-pointer text-base font-semibold text-gray-900 mb-4 hover:text-gray-700">
									Component Stack
								</summary>
								<pre className="text-sm mt-4 text-gray-700 whitespace-pre-wrap break-words bg-white p-4 rounded border border-gray-300">
									{errorInfo.componentStack}
								</pre>
							</details>
						)}
					</div>
				)}

				<div className="mt-12 pt-6 border-t border-gray-200 text-center">
					<p className="text-xs text-gray-500">
						Version {APP_VERSION}
					</p>
				</div>
			</div>
		</div>
	);
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = {
			hasError: false,
			error: null,
			errorInfo: null,
			showDetails: false,
			sentryEventId: null
		};
	}

	static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {

		this.setState({
			error,
			errorInfo,
		});

		console.warn('Uncaught error:', error, errorInfo);
	}

	toggleDetails = () => {
		this.setState(prevState => ({ showDetails: !prevState.showDetails }));
	};

	resetError = () => {
		this.setState({
			hasError: false,
			error: null,
			errorInfo: null,
			showDetails: false,
			sentryEventId: null
		});
	};

	handleRefresh = () => {
		window.location.reload();
	};

	render() {
		const { children = null } = this.props;
		const { hasError } = this.state;

		if (hasError) {
			return <ErrorBoundaryWithHooks state={this.state} toggleDetails={this.toggleDetails} handleRefresh={this.handleRefresh} resetError={this.resetError} />;
		}

		return children;
	}
}

export default ErrorBoundary;
